// First-avatar onboarding: where a freshly signed-in user should land, and the
// server state the /welcome page works from (see 20261011_avatar_onboarding.sql).
import { supabase } from '../config/supabase';
import type { Avatar } from './avatarService';
import { isQuestImmersiveApp, questNextAfterSignIn } from '../utils/questApp';

export interface OnboardingStatus {
  is_admin: boolean;
  has_avatar: boolean;
  gated: boolean;
  can_build: boolean;
  gen_remaining: number | null;
  starter: boolean;
  redeemed_code: boolean;
  /** Free starter avatar (20261027_free_starter_avatar.sql). */
  starter_claimed?: boolean;
  email_confirmed?: boolean;
  /** Can this account still claim a free starter look (and is one on offer)? */
  free_starter?: boolean;
}

export async function fetchOnboardingStatus(): Promise<OnboardingStatus | null> {
  const { data, error } = await supabase.rpc('my_onboarding_status');
  if (error) { console.warn('[onboarding] status failed', error.message); return null; }
  return data as OnboardingStatus | null;
}

/** A pre-built look a new account can take for free (copied instantly, no build). */
export interface StarterTemplate { look_id: string; name: string; tagline: string | null; thumbnail_url: string | null }

export async function fetchStarterTemplates(): Promise<StarterTemplate[]> {
  const { data, error } = await supabase.rpc('list_starter_templates');
  if (error) { console.warn('[onboarding] starter templates failed', error.message); return []; }
  return (data || []) as StarterTemplate[];
}

export interface ClaimResult { ok: boolean; reason?: string; message?: string; avatar?: Avatar }

/**
 * Claim the free starter avatar. Goes through /api/avatar/claim-starter (which
 * checks Turnstile and the per-IP limit); the new avatar comes back equipped.
 */
export async function claimStarterAvatar(look: string, turnstileToken: string | null): Promise<ClaimResult> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) return { ok: false, reason: 'auth', message: 'Sign in first.' };
  try {
    const res = await fetch('/api/avatar/claim-starter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-gathrxr-token': session.access_token },
      body: JSON.stringify({ look, turnstileToken }),
    });
    const data = await res.json().catch(() => null);
    if (data && typeof data.ok === 'boolean') return data as ClaimResult;
    return { ok: false, reason: 'error', message: 'Could not claim that avatar. Try again.' };
  } catch {
    return { ok: false, reason: 'network', message: "Can't reach GathrXR right now. Check your connection." };
  }
}

/**
 * Where the sign-up confirmation link should land: /welcome (signed in, free
 * robot pick), carrying on to `next` afterwards. Must stay inside the Supabase
 * redirect allow-list (https://gathrxr.com/**), else Supabase uses site_url.
 */
export function confirmRedirect(next?: string | null): string {
  const dest = safeNext(next);
  return `${window.location.origin}/welcome${dest === '/dashboard' ? '' : `?next=${encodeURIComponent(dest)}`}`;
}

/** Supabase's "Email not confirmed" sign-in error (email confirmation is on). */
export function isEmailNotConfirmed(err: { message?: string; code?: string } | null | undefined): boolean {
  return !!err && (err.code === 'email_not_confirmed' || /email not confirmed/i.test(err.message || ''));
}

/**
 * Send the sign-up confirmation email again. `redirectTo` is a full URL on
 * this site (default: /welcome) — the confirm link signs people in there.
 */
export async function resendConfirmation(email: string, captchaToken?: string | null, redirectTo?: string): Promise<{ ok: boolean; message: string }> {
  const { error } = await supabase.auth.resend({ type: 'signup', email, options: { emailRedirectTo: redirectTo || `${window.location.origin}/welcome`, captchaToken: captchaToken || undefined } });
  if (error) return { ok: false, message: /rate|too many|seconds/i.test(error.message) ? 'Please wait a minute before sending another email.' : 'Could not send the email. Try again shortly.' };
  return { ok: true, message: `Sent. Check ${email} for the link (and your spam folder).` };
}

/** What a redeemed code added to the account (20261021_code_grants.sql). */
export interface CodeGrants { builds: number; recolors: number; studio: number; npc_talk: number }

export async function redeemAvatarCode(code: string): Promise<{ ok: boolean; message: string; grants?: CodeGrants }> {
  const { data, error } = await supabase.rpc('redeem_avatar_code', { p_code: code });
  if (error) return { ok: false, message: 'Could not check that code. Try again.' };
  return data as { ok: boolean; message: string; grants?: CodeGrants };
}

const CODE_KEY = 'gx-pending-code';
export const rememberPendingCode = (code: string) => { try { localStorage.setItem(CODE_KEY, code); } catch { /* private mode */ } };
export const clearPendingCode = () => { try { localStorage.removeItem(CODE_KEY); } catch { /* private mode */ } };
export const pendingCode = () => { try { return localStorage.getItem(CODE_KEY); } catch { return null; } };

// Where a signed-out visitor was headed (a meetup or profile link) when they
// were sent to sign up. ?next= carries it normally; this covers the email
// confirmation round trip losing it, like the pending code above. The
// destination page clears it once it's done its signed-in step.
const RETURN_KEY = 'gx-pending-return';
const RETURN_TTL_MS = 24 * 60 * 60 * 1000;
export const rememberPendingReturn = (path: string) => {
  try { localStorage.setItem(RETURN_KEY, JSON.stringify({ path, at: Date.now() })); } catch { /* private mode */ }
};
export const clearPendingReturn = () => { try { localStorage.removeItem(RETURN_KEY); } catch { /* private mode */ } };
export const pendingReturn = (): string | null => {
  try {
    const v = JSON.parse(localStorage.getItem(RETURN_KEY) || 'null') as { path?: string; at?: number } | null;
    if (!v?.path || !v.at || Date.now() - v.at > RETURN_TTL_MS) return null;
    return safeNext(v.path, '') || null;
  } catch { return null; }
};
/** `next`, unless it's the default and a remembered meetup/profile link is waiting. */
export const withPendingReturn = (next: string) => (next === '/dashboard' ? pendingReturn() || next : next);

const skipKey = (uid: string) => `gx-welcome-skipped:${uid}`;
export const markWelcomeSkipped = (uid: string) => { try { localStorage.setItem(skipKey(uid), String(Date.now())); } catch { /* private mode */ } };
export const welcomeSkipped = (uid: string) => { try { return !!localStorage.getItem(skipKey(uid)); } catch { return false; } };

/** Only same-site paths are honoured for ?next= — never an absolute URL. */
export function safeNext(next: string | null | undefined, fallback = '/dashboard'): string {
  return next && next.startsWith('/') && !next.startsWith('//') ? next : fallback;
}

/**
 * After sign-in: people without an avatar who haven't skipped go to /welcome,
 * everyone else to `next`. Google users who still need a username also go to
 * /welcome (it opens on the username step).
 */
export async function routeAfterSignIn(nextIn: string): Promise<string> {
  // The installed immersive Quest app can't show 2D pages (/dashboard,
  // /welcome): always go back to the world it launched into / was last in.
  if (isQuestImmersiveApp()) return questNextAfterSignIn(safeNext(nextIn));
  const next = withPendingReturn(nextIn);
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return next;
  // A code link (/redeem?code=) applies the code first; that page sends
  // people on to /welcome afterwards. The code is also remembered across an
  // email-confirmation round trip, which loses ?next=.
  if (next.startsWith('/redeem')) return next;
  const pending = pendingCode();
  if (pending) return `/redeem?code=${encodeURIComponent(pending)}`;
  const needsName = user.app_metadata?.provider === 'google' && !user.user_metadata?.username_confirmed;
  if (needsName) return `/welcome?next=${encodeURIComponent(next)}`;
  if (welcomeSkipped(user.id)) return next;
  const status = await fetchOnboardingStatus();
  if (status && !status.has_avatar) return `/welcome?next=${encodeURIComponent(next)}`;
  return next;
}
